/**
 * Canonical SMS consent disclosure copy — the single source of what a person is actually shown.
 *
 * These strings are byte-identical to the server-side registry in
 * `database/migrations/2026-09-01-003-sms-consent-hardening.sql` (function `sms_consent_disclosure`),
 * which is what gets frozen into the append-only `sms_consent_events` evidence row.
 *
 * WHY THIS FILE EXISTS: the external audit's HIGH-1 finding was that displayed and stored copy had
 * drifted — the walk-in booth rendered third-person copy ("The registrant agrees…") while the
 * database recorded first-person copy ("By checking this box, I agree…"), so the "immutable evidence
 * contains the exact disclosure shown" claim was false. Rendering every surface FROM this module and
 * asserting equality with the migration (smsConsentDisclosures.test.ts) is that fix.
 *
 * RULES:
 *  - Never edit an existing v* string. Evidence rows already reference it; changing it retroactively
 *    falsifies what past registrants saw.
 *  - To change wording, add a new version here AND in a migration, then point the surface at it.
 *  - Each version is bound server-side to exactly one method + source, so a version cannot be
 *    replayed from a surface that never displayed it.
 *
 * LEGAL NOTE: v1-* freezes what is CURRENTLY displayed so the evidence is truthful. It is not an
 * endorsement of the wording; approved wording ships as v2-*.
 */

export const SMS_DISCLOSURE_VERSIONS = {
  /** GuestConfirmPage — the invited guest confirms their own ticket (self, first person). */
  guestConfirm: "v1-guest-2026-09",
  /** EventLandingPage — public event registration, standard and Range Night forms (self). */
  eventLanding: "v1-landing-2026-09",
  /** EventWalkinPanel — operator attests at the booth (operator_witnessed, third person). */
  walkinOperator: "v1-operator-2026-09",
} as const;

export type SmsDisclosureVersion =
  (typeof SMS_DISCLOSURE_VERSIONS)[keyof typeof SMS_DISCLOSURE_VERSIONS];

/** The surface identifier posted to the edge function, which maps it to the version above. */
export const SMS_CONSENT_SURFACES = {
  guestConfirm: "guest_confirm",
  eventLanding: "event_landing",
} as const;

export type SmsConsentSurface =
  (typeof SMS_CONSENT_SURFACES)[keyof typeof SMS_CONSENT_SURFACES];

export const SMS_DISCLOSURE_TEXT: Record<SmsDisclosureVersion, string> = {
  "v1-guest-2026-09":
    "By checking this box, I agree to receive recurring text messages from U.S. LawShield — event reminders, updates, and offers — at the number provided, including via automated technology. Msg & data rates may apply. Reply STOP to opt out, HELP for help.",
  "v1-landing-2026-09":
    "By checking this box and clicking submit (below), the registrant agrees to receive periodic messaging sent via automated technology to this phone number from U.S. and Texas LawShield, and the registrant also agrees to U.S. and Texas LawShield’s Texting/Messaging Terms and Conditions. Message and data rates may apply. Reply STOP to stop. You may receive additional program confirmation texts. Please see our Privacy Policy.",
  "v1-operator-2026-09":
    "The registrant agrees to receive recurring text messages from U.S. LawShield — event reminders, updates, and offers — at the number provided, including via automated technology. Msg & data rates may apply. Reply STOP to opt out, HELP for help.",
};

/**
 * Phrases inside a disclosure that render as links. The WORDS stay part of the canonical string
 * (so displayed text still equals stored text); only their presentation changes.
 */
export const SMS_DISCLOSURE_LINKS: ReadonlyArray<{
  phrase: string;
  href: string;
}> = [
  {
    phrase: "Terms and Conditions",
    href: "https://www.uslawshield.com/terms-and-conditions/",
  },
  {
    phrase: "Privacy Policy",
    href: "https://www.uslawshield.com/privacy-policy/",
  },
];

/**
 * Split a disclosure into plain-text and link segments for rendering. Segments concatenated back
 * together are exactly `SMS_DISCLOSURE_TEXT[version]` — that identity is asserted by the tests.
 */
export function splitDisclosureForRender(
  version: SmsDisclosureVersion,
): Array<{ text: string; href?: string }> {
  const source = SMS_DISCLOSURE_TEXT[version];
  const segments: Array<{ text: string; href?: string }> = [];
  let rest = source;

  while (rest.length > 0) {
    let bestIndex = -1;
    let best: { phrase: string; href: string } | null = null;
    for (const link of SMS_DISCLOSURE_LINKS) {
      const at = rest.indexOf(link.phrase);
      if (at !== -1 && (bestIndex === -1 || at < bestIndex)) {
        bestIndex = at;
        best = link;
      }
    }
    if (bestIndex === -1 || !best) {
      segments.push({ text: rest });
      break;
    }
    if (bestIndex > 0) {
      segments.push({ text: rest.slice(0, bestIndex) });
    }
    segments.push({ text: best.phrase, href: best.href });
    rest = rest.slice(bestIndex + best.phrase.length);
  }

  return segments;
}
